import { Injectable } from '@angular/core';
import { DocumentService } from '../../modules/document/document.service';

/**
 * Broad families a file can belong to. Drives which visual representation the
 * thumbnail renders (real preview vs. file-type tile) and which icon it uses.
 */
export type FileKind =
  | 'folder'
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'text'
  | 'archive'
  | 'code'
  | 'cad'
  | 'other';

/** Anything shaped like a document/attachment row coming back from the API. */
export interface FileLikeItem {
  type?: string | null;
  file_type?: string | null;
  mime_type?: string | null;
  original_name?: string | null;
  file_name?: string | null;
  name?: string | null;
}

/**
 * Single source of truth for "what kind of file is this and how should it look".
 *
 * Built on top of the type lists that already live in DocumentService
 * (allowedImageTypes / allowedVideoTypes / allowedAudioTypes) so the Documents
 * and Gallery widgets, the preview dialogs and the thumbnails all agree on the
 * same classification instead of each keeping its own extension array.
 */
@Injectable({ providedIn: 'root' })
export class FileTypeService {
  /** Extensions the browser can actually paint in an <img> tag. */
  private static readonly EXTRA_IMAGE_TYPES = ['tif', 'ico', 'heic', 'heif', 'avif'];

  private static readonly EXTRA_VIDEO_TYPES = ['m4v', '3gp', 'ogv', 'flv'];

  private static readonly WORD_TYPES = ['doc', 'docx', 'odt', 'rtf', 'dot', 'dotx'];
  private static readonly EXCEL_TYPES = ['xls', 'xlsx', 'xlsm', 'ods', 'csv', 'tsv'];
  private static readonly POWERPOINT_TYPES = ['ppt', 'pptx', 'pps', 'ppsx', 'odp'];
  private static readonly TEXT_TYPES = ['txt', 'md', 'log', 'rtf'];
  private static readonly ARCHIVE_TYPES = ['zip', 'rar', 'gz', 'tar', '7z', 'bz2', 'iso'];
  private static readonly CODE_TYPES = ['html', 'htm', 'xml', 'json', 'js', 'ts', 'css', 'scss', 'php', 'sql', 'yml', 'yaml'];
  private static readonly CAD_TYPES = ['dwg', 'dxf', 'dwf', 'rvt', 'ifc', 'skp'];

  private static readonly ICONS: Record<FileKind, string> = {
    folder: 'folder',
    image: 'image',
    video: 'videocam',
    audio: 'audiotrack',
    pdf: 'picture_as_pdf',
    word: 'description',
    excel: 'table_chart',
    powerpoint: 'slideshow',
    text: 'article',
    archive: 'folder_zip',
    code: 'code',
    cad: 'architecture',
    other: 'insert_drive_file',
  };

  constructor(private documentService: DocumentService) {}

  /**
   * Best-effort extension for an item: the `file_type` column when present,
   * otherwise the file name suffix, otherwise the mime subtype.
   */
  extensionOf(item: FileLikeItem | null | undefined): string {
    if (!item) return '';

    const rawType = (item.file_type || '').toString().toLowerCase().trim();
    // `file_type` is normally a bare extension, but some payloads carry a full
    // mime type ("application/pdf") — take the part after the slash.
    if (rawType && !rawType.includes('/')) {
      return rawType.replace(/^\./, '');
    }

    const fileName = (item.original_name || item.file_name || item.name || '').toString();
    const fromName = fileName.includes('.') ? fileName.split('.').pop() : '';
    if (fromName) return fromName.toLowerCase().trim();

    const mime = (item.mime_type || rawType || '').toString().toLowerCase();
    return mime.includes('/') ? mime.split('/').pop()!.trim() : '';
  }

  /** Classify an item into the family that drives its visual representation. */
  kindOf(item: FileLikeItem | null | undefined): FileKind {
    if (!item) return 'other';
    if ((item.type || '').toString().toLowerCase() === 'folder') return 'folder';

    const ext = this.extensionOf(item);
    const mime = (item.mime_type || '').toString().toLowerCase();

    if (this.imageTypes().includes(ext) || mime.startsWith('image/')) return 'image';
    if (this.videoTypes().includes(ext) || mime.startsWith('video/')) return 'video';
    if (this.audioTypes().includes(ext) || mime.startsWith('audio/')) return 'audio';
    if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
    if (FileTypeService.WORD_TYPES.includes(ext)) return 'word';
    if (FileTypeService.EXCEL_TYPES.includes(ext)) return 'excel';
    if (FileTypeService.POWERPOINT_TYPES.includes(ext)) return 'powerpoint';
    if (FileTypeService.ARCHIVE_TYPES.includes(ext)) return 'archive';
    if (FileTypeService.CAD_TYPES.includes(ext)) return 'cad';
    if (FileTypeService.CODE_TYPES.includes(ext)) return 'code';
    if (FileTypeService.TEXT_TYPES.includes(ext) || mime.startsWith('text/')) return 'text';

    return 'other';
  }

  isImage(item: FileLikeItem | null | undefined): boolean {
    return this.kindOf(item) === 'image';
  }

  isVideo(item: FileLikeItem | null | undefined): boolean {
    return this.kindOf(item) === 'video';
  }

  isAudio(item: FileLikeItem | null | undefined): boolean {
    return this.kindOf(item) === 'audio';
  }

  /** Material icon name for a family (or for an item, via `kindOf`). */
  iconFor(kind: FileKind): string {
    return FileTypeService.ICONS[kind] || FileTypeService.ICONS.other;
  }

  iconForItem(item: FileLikeItem | null | undefined): string {
    return this.iconFor(this.kindOf(item));
  }

  private imageTypes(): string[] {
    return [
      ...this.documentService.allowedImageTypes(),
      ...FileTypeService.EXTRA_IMAGE_TYPES,
    ];
  }

  private videoTypes(): string[] {
    return [
      ...this.documentService.allowedVideoTypes(),
      ...FileTypeService.EXTRA_VIDEO_TYPES,
    ];
  }

  private audioTypes(): string[] {
    return this.documentService.allowedAudioTypes();
  }
}
